import { formatServings, formatTotalDuration, joinQuantityUnit } from './formatting';
import type { Ingredient, Step } from './schema';

/**
 * Recette en texte brut, pour l'envoyer dans un message (US-33).
 *
 * C'est le format d'échange **réel** entre amis : on colle une recette dans
 * une conversation, on ne demande pas à quelqu'un d'installer une application.
 * Un lien et un PDF existent à côté, mais celui-ci est celui qui marche
 * partout, y compris dans un SMS.
 *
 * Aucune mise en forme qui dépendrait d'un rendu : pas de gras, pas de
 * tableau. Des tirets et des lignes vides, qui survivent à n'importe quelle
 * messagerie.
 */
export interface RecipeTextInput {
  title: string;
  description: string | null;
  servings: number | null;
  servings_label: string | null;
  prep_min: number | null;
  rest_min: number | null;
  cook_min: number | null;
  source_name: string | null;
  author: string | null;
  source_url: string | null;
  ingredients: readonly Pick<Ingredient, 'quantity' | 'unit' | 'name' | 'note' | 'is_optional'>[];
  steps: readonly Pick<Step, 'instruction'>[];
}

export function recipeToText(recipe: RecipeTextInput): string {
  const lines: string[] = [recipe.title.toUpperCase(), ''];

  if (recipe.description !== null) {
    lines.push(recipe.description, '');
  }

  const meta = [
    formatServings(recipe.servings, recipe.servings_label),
    formatTotalDuration(recipe)?.label ?? null,
  ].filter((part): part is string => part !== null);
  if (meta.length > 0) lines.push(meta.join(' · '), '');

  if (recipe.ingredients.length > 0) {
    lines.push('INGRÉDIENTS');
    for (const ingredient of recipe.ingredients) {
      const quantity = ingredient.quantity === null ? null : String(ingredient.quantity);
      const measure = joinQuantityUnit(quantity, ingredient.unit, ingredient.quantity);
      const note = ingredient.note === null ? '' : ` (${ingredient.note})`;
      const optional = ingredient.is_optional === 1 ? ' — facultatif' : '';
      lines.push(`- ${measure === '' ? '' : `${measure} `}${ingredient.name}${note}${optional}`);
    }
    lines.push('');
  }

  if (recipe.steps.length > 0) {
    lines.push('PRÉPARATION');
    // Numérotées : dans un message, rien d'autre ne dit qu'une ligne est une
    // étape et non un ingrédient de plus.
    recipe.steps.forEach((step, index) => {
      lines.push(`${index + 1}. ${step.instruction}`);
    });
    lines.push('');
  }

  const origin = [
    recipe.author === null ? null : `De ${recipe.author}`,
    recipe.source_name,
    recipe.source_url,
  ].filter((part): part is string => part !== null && part.length > 0);
  if (origin.length > 0) lines.push(origin.join(' · '));

  // Une seule ligne vide finale, quel que soit le nombre de blocs présents.
  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trimEnd();
}
